import { useState } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { Box, BoxProps, Flex } from '@chakra-ui/react';
import { Button, Tabs, Text } from 'components/UI';
import { validateRequired } from 'utils/validation';
import ErrorPanel from 'components/common/ErrorPanel';
import { formatErrors } from 'utils/helpers';
import { IOption, ISettingsValues } from 'types';
import { checkProductManager } from 'utils/pages';
import { IRoleResource } from '../../../../../server/types';
import InfoForm from './InfoForm';
import RelationForm from './RelationForm';

export type RoleFormValues = Omit<IRoleResource, 'id' | '_id' | 'createdAt' | 'updatedAt' | 'pages'> & {
  pages: string[];
  products: string[];
};

export type RoleFormProps = {
  onSubmit: (values: RoleFormValues, actions: FormikHelpers<RoleFormValues>) => void;
  initialValues?: RoleFormValues;
  error?: string;
  data?: Partial<IRoleResource>;
  buttonText: string;
  pageOptions: IOption[];
  productOptions: IOption[];
  specificationOptions: IOption[];
  settings: ISettingsValues | Record<string, never>;
};

const RoleTabPanel = (props: BoxProps) => <Box bgColor="base2Colour.500" pt={5} pb={6} px={4} h={306} {...props} />;

const RoleForm = ({
  onSubmit,
  initialValues = {
    name: '',
    isDefault: false,
    isAnonymous: false,
    products: [],
    pages: [],
    specifications: [],
  },
  pageOptions,
  productOptions,
  specificationOptions,
  data,
  error,
  settings,
  buttonText,
}: RoleFormProps) => {
  const [submitted, setSubmitted] = useState(false);
  return (
    <Formik
      initialValues={(data as RoleFormValues) ?? initialValues ?? {}}
      validate={async (values) => {
        const errors = validateRequired<RoleFormValues>(['name'], values);
        return errors;
      }}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => {
        const renderErrors =
          submitted && (error || (errors && Object.keys(errors).length > 0)) ? (
            <ErrorPanel
              title="The following errors occurred during submission:"
              errors={error ? [error] : formatErrors(errors as Record<string, string>)}
            />
          ) : null;

        const isProductManager = checkProductManager(values.pages);

        const relationProps = {
          data,
          values,
          submitted,
          touched,
          errors,
          setFieldValue,
          onChange: handleChange,
          onBlur: handleBlur,
        };

        const renderPageInfoText = !values.isAnonymous ? (
          <Text mb={4}>
            The &quot;Configurator&quot; and &quot;Sign Out&quot; navigation items are available for all roles.
          </Text>
        ) : null;

        const tabOptions = [
          {
            label: 'INFO',
            panel: (
              <RoleTabPanel>
                <InfoForm
                  data={data}
                  values={values}
                  submitted={submitted}
                  touched={touched}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  settings={settings}
                />
              </RoleTabPanel>
            ),
          },
          {
            label: 'PAGES',
            panel: (
              <RoleTabPanel>
                {renderPageInfoText}
                <RelationForm
                  name="pages"
                  label="Pages"
                  options={pageOptions}
                  anonymous={values.isAnonymous}
                  {...relationProps}
                />
              </RoleTabPanel>
            ),
          },
          {
            label: 'PRODUCTS',
            panel: (
              <RoleTabPanel>
                <RelationForm
                  name="products"
                  label="Products"
                  options={productOptions}
                  fullAccess={isProductManager}
                  {...relationProps}
                />
              </RoleTabPanel>
            ),
          },
          {
            label: 'SPECIFICATIONS',
            panel: (
              <RoleTabPanel>
                <RelationForm
                  name="specifications"
                  label="Specifications"
                  options={specificationOptions}
                  {...relationProps}
                />
              </RoleTabPanel>
            ),
          },
        ];

        return (
          <form style={{ width: '100%' }} onSubmit={handleSubmit}>
            {renderErrors}
            <Box mb={4}>
              <Tabs options={tabOptions} panelProps={{ bgColor: 'base2Colour.400', p: 4 }} variant="line" />
            </Box>

            <Flex justifyContent="flex-end">
              <Button
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                onClick={() => setSubmitted(true)}
                type="submit"
              >
                {buttonText}
              </Button>
            </Flex>
          </form>
        );
      }}
    </Formik>
  );
};

export default RoleForm;
